import type {
    AlignOpts,
    Anchor,
    AnimCtx,
    BBoxHandle,
    ChannelState,
    FitMethods,
    FitToBBoxOpts,
    GroupBuilder,
    GroupObject,
    LayoutBuilder,
    LayoutOpts,
    NumExpr,
    NumFn,
    ObjRef,
    Padding,
    PinConstraint,
    PinMethods,
    Rect,
    RectFn,
    ResizeMethods,
    ResizeTarget,
    ResizeTargetExpr,
    ResolvedCtx,
    SceneGeometry,
    SceneObject,
    Selection,
    Transform,
    TransformMethods,
    Vec2,
    Vec2Expr,
    Vec2Fn,
} from './types';
import { isGroup, isExpr } from './types';
import { applySR, lerp, vec } from './math';

/** Flatten ObjRef[] to a flat SceneObject[] (expands GroupObjects) */
function flattenObjRefs(objects: ObjRef[]): SceneObject[] {
    const result: SceneObject[] = [];
    for (const obj of objects) {
        if (isGroup(obj)) {
            result.push(...obj.members);
        } else {
            result.push(obj as SceneObject);
        }
    }
    return result;
}

export interface DebugGroupInfo {
    members: SceneObject[];
    kind: 'group' | 'layout';
    pin?: {
        anchor: Anchor;
        target: Vec2;
    };
}

interface DeferredPinTarget {
    anchor: Anchor;
    targetExpr: Vec2Expr;
    u: number;
    fromWorld: Vec2;
    /** Timeline segment body that registered this pin (see timeline passes). */
    segmentId: number;
}

class BBoxHandleImpl implements BBoxHandle {
    private paddingValues = { top: 0, right: 0, bottom: 0, left: 0 };
    // Store a function that computes the rect lazily
    private computeRect: () => Rect;

    constructor(computeRect: () => Rect) {
        this.computeRect = computeRect;
    }

    pad(p: Padding): BBoxHandle {
        const parentCompute = this.computeRect;
        const parentPadding = this.paddingValues;

        let newPadding: typeof this.paddingValues;
        if (typeof p === 'number') {
            newPadding = {
                top: parentPadding.top + p,
                right: parentPadding.right + p,
                bottom: parentPadding.bottom + p,
                left: parentPadding.left + p,
            };
        } else {
            newPadding = {
                top: parentPadding.top + (p.top ?? 0),
                right: parentPadding.right + (p.right ?? 0),
                bottom: parentPadding.bottom + (p.bottom ?? 0),
                left: parentPadding.left + (p.left ?? 0),
            };
        }

        const result = new BBoxHandleImpl(parentCompute);
        result.paddingValues = newPadding;
        return result;
    }

    private getRect(): Rect {
        const baseRect = this.computeRect();
        return {
            x: baseRect.x - this.paddingValues.left,
            y: baseRect.y - this.paddingValues.top,
            w: baseRect.w + this.paddingValues.left + this.paddingValues.right,
            h: baseRect.h + this.paddingValues.top + this.paddingValues.bottom,
        };
    }

    size(): Vec2Fn {
        return (_ctx: ResolvedCtx): Vec2 => {
            const r = this.getRect();
            return [r.w, r.h];
        };
    }

    width(): NumFn {
        return (_ctx: ResolvedCtx): number => {
            return this.getRect().w;
        };
    }

    height(): NumFn {
        return (_ctx: ResolvedCtx): number => {
            return this.getRect().h;
        };
    }

    whSize(): { width: NumFn; height: NumFn } {
        return {
            width: this.width(),
            height: this.height(),
        };
    }

    center(): Vec2Fn {
        return (_ctx: ResolvedCtx): Vec2 => {
            const r = this.getRect();
            return [r.x + r.w / 2, r.y + r.h / 2];
        };
    }

    anchor(a: Anchor): Vec2Fn {
        return (_ctx: ResolvedCtx): Vec2 => {
            const r = this.getRect();
            let x = r.x, y = r.y;

            // Horizontal position
            if (a === 'top' || a === 'center' || a === 'bottom' || a === 'baseline') {
                x = r.x + r.w / 2;
            } else if (a === 'topRight' || a === 'right' || a === 'bottomRight') {
                x = r.x + r.w;
            }

            // Vertical position
            if (a === 'left' || a === 'center' || a === 'right') {
                y = r.y + r.h / 2;
            } else if (a === 'bottomLeft' || a === 'bottom' || a === 'bottomRight' || a === 'baselineLeft' || a === 'baseline') {
                y = r.y + r.h;
            }

            return [x, y];
        };
    }

    toRect(): RectFn {
        return (_ctx: ResolvedCtx): Rect => {
            return this.getRect();
        };
    }
}

export class AnimCtxImpl implements AnimCtx {
    private base = new Map<SceneObject, Transform>();
    private channels = new Map<SceneObject, ChannelState>();
    private pins = new Map<SceneObject, PinConstraint>();
    private scene: SceneGeometry | null = null;
    private debugGroups: DebugGroupInfo[] = [];
    /** Track group pivots for rotation - set when group is pinned */
    private groupPivots = new Map<symbol, Vec2>();
    /** Cache groups by members - same members (any order) = same group */
    private groupCache: GroupObject[] = [];
    private deferredPins = new Map<SceneObject, DeferredPinTarget>();
    /** True while deferred pins are being resolved, to suppress reentrant flushing. */
    private resolvingDeferred = false;

    /**
     * Sequential/overlapping motions need their position constraints frozen at
     * the state they had when their own motion ended — otherwise a distance /
     * orbit / follow-path pin from a finished motion keeps tracking a target
     * that moves during later motions. The timeline evaluates the graph in
     * multiple passes (one per motion end time, then the display time). Each
     * pass runs every started segment body; a segment that has ended contributes
     * the concrete pins captured at its end (`frozenSegmentPins`) instead of
     * re-tracking. A later motion re-pinning the same object still wins via the
     * normal last-write ordering, so hand-offs keep working.
     */
    private frozenSegmentPins = new Map<number, Map<SceneObject, PinConstraint>>();
    /** Segment id whose body is currently running. */
    private currentSegmentId = -1;
    /** Objects each segment pinned during the current pass (for freezing). */
    private segmentPinnedObjects = new Map<number, Set<SceneObject>>();

    /** Resolve a Vec2 expression to a concrete Vec2 */
    private resolveVec2(expr: Vec2Expr): Vec2 {
        if (isExpr(expr)) {
            // Create a minimal ResolvedCtx for expression evaluation
            const ctx = this.createResolvedCtx();
            return expr(ctx);
        }
        return expr;
    }

    /** Resolve a number expression to a concrete number */
    private resolveNum(expr: NumExpr): number {
        if (isExpr(expr)) {
            const ctx = this.createResolvedCtx();
            return expr(ctx);
        }
        return expr;
    }

    /** Create a ResolvedCtx based on current state */
    private createResolvedCtx(): ResolvedCtx {
        if (!this.scene) throw new Error('Scene not set');
        // Use solve() to get the current resolved state
        return this.solve(this.scene);
    }

    private getInitialOpacity(obj: SceneObject): number {
        return this.scene?.getInitialOpacity?.(obj) ?? 1;
    }

    private getInitialProp(obj: SceneObject, name: string): number | undefined {
        return this.scene?.getInitialProp?.(obj, name);
    }

    reset(scene: SceneGeometry) {
        this.base.clear();
        this.channels.clear();
        this.pins.clear();
        this.groupPivots.clear();
        this.groupCache = [];
        this.deferredPins.clear();
        this.debugGroups = [];
        this.scene = scene;
        this.frozenSegmentPins.clear();
        this.currentSegmentId = -1;
        this.segmentPinnedObjects.clear();

        for (const obj of scene.objects()) {
            this.base.set(obj, scene.getInitialTransform(obj));
        }
    }

    /**
     * Start a fresh timeline pass. Clears the per-pass transform state (channels,
     * pins, deferred pins) but keeps the frozen pins captured from motions that
     * ended in earlier passes.
     */
    beginTimelinePass(): void {
        this.channels.clear();
        this.pins.clear();
        this.deferredPins.clear();
        this.groupPivots.clear();
        this.groupCache = [];
        this.debugGroups = [];
        this.segmentPinnedObjects.clear();
        this.currentSegmentId = -1;
    }

    /** Mark which segment body is about to run so pins can be attributed to it. */
    beginSegmentScope(segmentId: number): void {
        this.currentSegmentId = segmentId;
    }

    /**
     * Finish a pass: resolve any still-deferred pins, then capture the resolved
     * pins of the segments that end at this pass's boundary so later passes can
     * replay them as frozen constraints.
     */
    endTimelinePass(freezeSegmentIds: number[]): void {
        if (this.scene) {
            this.resolveDeferredPins(this.scene);
        }
        for (const segmentId of freezeSegmentIds) {
            const pinnedObjects = this.segmentPinnedObjects.get(segmentId);
            if (!pinnedObjects || pinnedObjects.size === 0) {
                continue;
            }
            let frozen = this.frozenSegmentPins.get(segmentId);
            if (!frozen) {
                frozen = new Map();
                this.frozenSegmentPins.set(segmentId, frozen);
            }
            for (const obj of pinnedObjects) {
                const pin = this.pins.get(obj);
                if (pin) {
                    frozen.set(obj, { ...pin });
                }
            }
        }
    }

    /** Record that the current segment pinned this object during the pass. */
    private notePinnedObject(obj: SceneObject): void {
        if (this.currentSegmentId < 0) {
            return;
        }
        let set = this.segmentPinnedObjects.get(this.currentSegmentId);
        if (!set) {
            set = new Set();
            this.segmentPinnedObjects.set(this.currentSegmentId, set);
        }
        set.add(obj);
    }

    /**
     * If the current segment has frozen pins from a previous pass, apply the
     * frozen value for this object and report that tracking should be skipped.
     */
    private applyFrozenPinIfAny(obj: SceneObject, anchor: Anchor): boolean {
        const frozen = this.frozenSegmentPins.get(this.currentSegmentId)?.get(obj);
        if (!frozen) {
            return false;
        }
        this.pins.set(obj, { anchor: frozen.anchor ?? anchor, world: frozen.world });
        this.notePinnedObject(obj);
        return true;
    }

    getDebugGroups(): DebugGroupInfo[] {
        return this.debugGroups;
    }

    getDebugPins(): Map<SceneObject, PinConstraint> {
        return this.pins;
    }

    /** Resolve an object's or group's current anchor in world coordinates. */
    anchorPosition(obj: SceneObject | GroupObject, anchor: Anchor): Vec2 {
        return isGroup(obj)
            ? this.resolveGroupAnchor(obj, anchor)
            : this.getCurrentAnchorPosition(obj, anchor);
    }

    /**
     * True when this object already has a position constraint (an applied pin or a
     * pending deferred pin). Position behaviors (pin/offset/distance/orbit) fix the
     * object's placement, and solve() rotates around the pinned anchor, so callers
     * can avoid adding a redundant rotation-center pin that would otherwise clobber
     * a not-yet-resolved deferred position pin.
     */
    hasPositionConstraint(obj: SceneObject): boolean {
        return this.pins.has(obj) || this.deferredPins.has(obj);
    }

    resolveDeferredPins(scene: SceneGeometry): void {
        if (this.deferredPins.size === 0) {
            return;
        }

        this.resolvingDeferred = true;
        try {
            const resolvedCtx = this.solve(scene);
            for (const [obj, deferred] of this.deferredPins) {
                const target = isExpr(deferred.targetExpr) ? deferred.targetExpr(resolvedCtx) : deferred.targetExpr;
                const world = vec.lerp(deferred.fromWorld, target, deferred.u);
                this.pins.set(obj, {
                    anchor: deferred.anchor,
                    world,
                });
            }
        } finally {
            this.resolvingDeferred = false;
        }
        this.deferredPins.clear();
    }

    /**
     * Commit a single object's pending deferred pin into a concrete pin.
     *
     * Expression-based pins (e.g. follow-path) are deferred so intra-motion
     * dependencies can resolve after the whole timeline is evaluated. But when a
     * later motion in the same evaluation reads this object's current position to
     * continue from it, the previous motion's deferred pin must already be
     * materialized — otherwise continuity falls back to the object's initial
     * transform and the animation appears to "reset". Flushing on demand keeps
     * sequential motions chained to their transient state.
     */
    private flushDeferredPin(obj: SceneObject): void {
        // Never flush while a deferred pin is being resolved: resolution reads an
        // object's own bbox/anchor (e.g. to infer an orbit radius) and must see
        // the pre-pin state, not a prematurely committed one.
        if (this.resolvingDeferred) {
            return;
        }
        const deferred = this.deferredPins.get(obj);
        if (!deferred || !this.scene) {
            return;
        }
        // Delete before resolving so any self-referential expression terminates.
        this.deferredPins.delete(obj);
        this.resolvingDeferred = true;
        try {
            const resolvedCtx = this.solve(this.scene);
            const target = isExpr(deferred.targetExpr) ? deferred.targetExpr(resolvedCtx) : deferred.targetExpr;
            const world = vec.lerp(deferred.fromWorld, target, deferred.u);
            this.pins.set(obj, {
                anchor: deferred.anchor,
                world,
            });
        } finally {
            this.resolvingDeferred = false;
        }
    }

    /** Get the current world position of a specific anchor on an object */
    private getCurrentAnchorPosition(obj: SceneObject, anchor: Anchor): Vec2 {
        if (!this.scene) throw new Error('Scene not set');

        // Materialize any pending deferred pin so continuity reads the object's
        // transient position rather than its initial transform.
        this.flushDeferredPin(obj);

        // If there's an existing pin with this anchor, return its world position
        const existingPin = this.pins.get(obj);
        if (existingPin && existingPin.anchor === anchor) {
            return existingPin.world;
        }

        // Otherwise, compute where this anchor currently is in world space
        const base = this.base.get(obj)!;
        const ch = this.channels.get(obj);

        // Get current transform values
        const scale = ch?.scale ?? base.scale;
        const scaleX = (ch?.scaleX ?? 1) * scale;
        const scaleY = (ch?.scaleY ?? 1) * scale;
        const rotate = ch?.rotate ?? base.rotate;

        // Get current origin position
        let originPos: Vec2;
        if (existingPin) {
            // If pinned by a different anchor, compute origin from that pin
            const pinnedLocal = this.scene.resolveAnchor(obj, existingPin.anchor);
            const cos = Math.cos(rotate);
            const sin = Math.sin(rotate);
            const transformedX = pinnedLocal[0] * scaleX * cos - pinnedLocal[1] * scaleY * sin;
            const transformedY = pinnedLocal[0] * scaleX * sin + pinnedLocal[1] * scaleY * cos;
            originPos = [existingPin.world[0] - transformedX, existingPin.world[1] - transformedY];
        } else {
            originPos = base.translate;
        }

        // Get local anchor offset and transform it
        const local = this.scene.resolveAnchor(obj, anchor);
        const cos = Math.cos(rotate);
        const sin = Math.sin(rotate);
        const transformedX = local[0] * scaleX * cos - local[1] * scaleY * sin;
        const transformedY = local[0] * scaleX * sin + local[1] * scaleY * cos;

        return [originPos[0] + transformedX, originPos[1] + transformedY];
    }

    private getGroupBounds(group: GroupObject): Rect {
        // Delegate to computeGroupWorldBounds which correctly handles non-origin pins
        return this.computeGroupWorldBounds(group);
    }

    /** Get the origin position of an object in world space, accounting for any existing pin */
    private getOriginPosition(obj: SceneObject): Vec2 {
        if (!this.scene) throw new Error('Scene not set');

        const pin = this.pins.get(obj);
        if (!pin) {
            return this.base.get(obj)!.translate;
        }

        // Compute origin from pin: origin = pinWorld - transformed(localAnchor)
        const base = this.base.get(obj)!;
        const ch = this.channels.get(obj);
        const scale = ch?.scale ?? base.scale;
        const scaleX = (ch?.scaleX ?? 1) * scale;
        const scaleY = (ch?.scaleY ?? 1) * scale;
        const rotate = ch?.rotate ?? base.rotate;

        const localAnchor = this.scene.resolveAnchor(obj, pin.anchor);
        const cos = Math.cos(rotate);
        const sin = Math.sin(rotate);
        const transformedX = localAnchor[0] * scaleX * cos - localAnchor[1] * scaleY * sin;
        const transformedY = localAnchor[0] * scaleX * sin + localAnchor[1] * scaleY * cos;

        return [pin.world[0] - transformedX, pin.world[1] - transformedY];
    }

    private resolveGroupAnchor(group: GroupObject, anchor: Anchor): Vec2 {
        const bounds = this.getGroupBounds(group);

        // Map anchor to position within bounds
        const anchorMap: Record<Anchor, Vec2> = {
            origin: [bounds.x, bounds.y], // For groups, origin is same as topLeft
            topLeft: [bounds.x, bounds.y],
            top: [bounds.x + bounds.w / 2, bounds.y],
            topRight: [bounds.x + bounds.w, bounds.y],
            left: [bounds.x, bounds.y + bounds.h / 2],
            center: [bounds.x + bounds.w / 2, bounds.y + bounds.h / 2],
            right: [bounds.x + bounds.w, bounds.y + bounds.h / 2],
            bottomLeft: [bounds.x, bounds.y + bounds.h],
            bottom: [bounds.x + bounds.w / 2, bounds.y + bounds.h],
            bottomRight: [bounds.x + bounds.w, bounds.y + bounds.h],
            baselineLeft: [bounds.x, bounds.y + bounds.h], // baseline defaults to bottom
            baseline: [bounds.x + bounds.w / 2, bounds.y + bounds.h],
        };

        return anchorMap[anchor];
    }

    private getGroupCenter(group: GroupObject): Vec2 {
        const bounds = this.getGroupBounds(group);
        return [bounds.x + bounds.w / 2, bounds.y + bounds.h / 2];
    }

    scale(obj: SceneObject | GroupObject): TransformMethods {
        if (isGroup(obj)) {
            // For groups, scale() is RELATIVE - it multiplies each member's scale by the factor
            // This preserves relative sizes within the group
            return {
                to: (vExpr: NumExpr, u: number) => {
                    const v = this.resolveNum(vExpr);
                    const groupCenter = this.getGroupCenter(obj);
                    // Interpolated scale factor: 1 -> v
                    const scaleFactor = lerp(1, v, u);

                    for (const member of obj.members) {
                        const base = this.base.get(member)!;
                        const ch = this.channels.get(member) ?? {};
                        const currentScale = ch.scale ?? base.scale;

                        // IMPORTANT: Get member center BEFORE modifying ch.scale.
                        // Resolve the anchor through scene geometry so top-left-origin,
                        // rotated external models do not jump when a scale tween starts.
                        const memberCenter = this.getCurrentAnchorPosition(member, 'center');

                        // Now multiply by scale factor to preserve relative sizes
                        ch.scale = currentScale * scaleFactor;

                        // Scale member center position around group center
                        const offsetFromGroupCenter = vec.sub(memberCenter, groupCenter);
                        const scaledOffset: Vec2 = [
                            offsetFromGroupCenter[0] * scaleFactor,
                            offsetFromGroupCenter[1] * scaleFactor
                        ];
                        const newMemberCenter = vec.add(groupCenter, scaledOffset);

                        // Pin member's center to the scaled position
                        this.pins.set(member, {
                            anchor: 'center',
                            world: newMemberCenter,
                        });
                        this.channels.set(member, ch);
                    }
                },
                by: (deltaExpr: NumExpr, u: number) => {
                    const delta = this.resolveNum(deltaExpr);
                    const groupCenter = this.getGroupCenter(obj);
                    const scaleFactor = lerp(1, 1 + delta, u);

                    for (const member of obj.members) {
                        const base = this.base.get(member)!;
                        const ch = this.channels.get(member) ?? {};
                        const currentScale = ch.scale ?? base.scale;

                        // IMPORTANT: Get member center BEFORE modifying ch.scale.
                        // Resolve the anchor through scene geometry so top-left-origin,
                        // rotated external models do not jump when a scale tween starts.
                        const memberCenter = this.getCurrentAnchorPosition(member, 'center');

                        ch.scale = currentScale * scaleFactor;

                        // Scale member center position around group center
                        const offsetFromGroupCenter = vec.sub(memberCenter, groupCenter);
                        const scaledOffset: Vec2 = [
                            offsetFromGroupCenter[0] * scaleFactor,
                            offsetFromGroupCenter[1] * scaleFactor
                        ];
                        const newMemberCenter = vec.add(groupCenter, scaledOffset);

                        // Pin member's center to the scaled position
                        this.pins.set(member, {
                            anchor: 'center',
                            world: newMemberCenter,
                        });
                        this.channels.set(member, ch);
                    }
                }
            };
        }

        return {
            to: (vExpr: NumExpr, u: number) => {
                const v = this.resolveNum(vExpr);
                const ch = this.channels.get(obj) ?? {};
                // Use current scale for continuity between tweens
                const current = ch.scale ?? this.base.get(obj)!.scale;
                ch.scale = lerp(current, v, u);
                this.channels.set(obj, ch);
            },

            by: (deltaExpr: NumExpr, u: number) => {
                const delta = this.resolveNum(deltaExpr);
                const ch = this.channels.get(obj) ?? {};
                const current = ch.scale ?? this.base.get(obj)!.scale;
                ch.scale = current * lerp(1, 1 + delta, u);
                this.channels.set(obj, ch);
            }
        };
    }

    scaleX(obj: SceneObject): TransformMethods {
        return {
            to: (vExpr: NumExpr, u: number) => {
                const v = this.resolveNum(vExpr);
                const ch = this.channels.get(obj) ?? {};
                const current = ch.scaleX ?? 1;
                ch.scaleX = lerp(current, v, u);
                this.channels.set(obj, ch);
            },
            by: (deltaExpr: NumExpr, u: number) => {
                const delta = this.resolveNum(deltaExpr);
                const ch = this.channels.get(obj) ?? {};
                const current = ch.scaleX ?? 1;
                ch.scaleX = current * lerp(1, 1 + delta, u);
                this.channels.set(obj, ch);
            }
        };
    }

    scaleY(obj: SceneObject): TransformMethods {
        return {
            to: (vExpr: NumExpr, u: number) => {
                const v = this.resolveNum(vExpr);
                const ch = this.channels.get(obj) ?? {};
                const current = ch.scaleY ?? 1;
                ch.scaleY = lerp(current, v, u);
                this.channels.set(obj, ch);
            },
            by: (deltaExpr: NumExpr, u: number) => {
                const delta = this.resolveNum(deltaExpr);
                const ch = this.channels.get(obj) ?? {};
                const current = ch.scaleY ?? 1;
                ch.scaleY = current * lerp(1, 1 + delta, u);
                this.channels.set(obj, ch);
            }
        };
    }

    width(obj: SceneObject): TransformMethods {
        if (!this.scene) throw new Error('Scene not set');
        const bounds = this.scene.getBounds(obj);
        const originalWidth = bounds.w;

        return {
            to: (targetPxExpr: NumExpr, u: number) => {
                const targetPx = this.resolveNum(targetPxExpr);
                const targetScaleX = targetPx / originalWidth;
                const ch = this.channels.get(obj) ?? {};
                const current = ch.scaleX ?? 1;
                ch.scaleX = lerp(current, targetScaleX, u);
                this.channels.set(obj, ch);
            },
            by: (deltaPxExpr: NumExpr, u: number) => {
                const deltaPx = this.resolveNum(deltaPxExpr);
                const ch = this.channels.get(obj) ?? {};
                const currentScaleX = ch.scaleX ?? 1;
                const currentWidth = originalWidth * currentScaleX;
                const targetWidth = currentWidth + deltaPx;
                const targetScaleX = targetWidth / originalWidth;
                ch.scaleX = lerp(currentScaleX, targetScaleX, u);
                this.channels.set(obj, ch);
            }
        };
    }

    height(obj: SceneObject): TransformMethods {
        if (!this.scene) throw new Error('Scene not set');
        const bounds = this.scene.getBounds(obj);
        const originalHeight = bounds.h;

        return {
            to: (targetPxExpr: NumExpr, u: number) => {
                const targetPx = this.resolveNum(targetPxExpr);
                const targetScaleY = targetPx / originalHeight;
                const ch = this.channels.get(obj) ?? {};
                const current = ch.scaleY ?? 1;
                ch.scaleY = lerp(current, targetScaleY, u);
                this.channels.set(obj, ch);
            },
            by: (deltaPxExpr: NumExpr, u: number) => {
                const deltaPx = this.resolveNum(deltaPxExpr);
                const ch = this.channels.get(obj) ?? {};
                const currentScaleY = ch.scaleY ?? 1;
                const currentHeight = originalHeight * currentScaleY;
                const targetHeight = currentHeight + deltaPx;
                const targetScaleY = targetHeight / originalHeight;
                ch.scaleY = lerp(currentScaleY, targetScaleY, u);
                this.channels.set(obj, ch);
            }
        };
    }

    resize(obj: SceneObject): ResizeMethods {
        if (!this.scene) throw new Error('Scene not set');
        const bounds = this.scene.getBounds(obj);
        const originalWidth = bounds.w;
        const originalHeight = bounds.h;

        // Resolve any expressions in the resize target
        const resolveTarget = (target: ResizeTargetExpr): ResizeTarget => {
            if (typeof target === 'number') return target;
            if (typeof target === 'function') return this.resolveNum(target);
            if ('fit' in target) return { fit: this.resolveNum(target.fit) };
            if ('width' in target && 'height' in target) {
                return { width: this.resolveNum(target.width), height: this.resolveNum(target.height) };
            }
            if ('width' in target) return { width: this.resolveNum(target.width) };
            if ('height' in target) return { height: this.resolveNum(target.height) };
            return target as ResizeTarget;
        };

        const parseTarget = (target: ResizeTarget): { scaleX?: number; scaleY?: number; scale?: number } => {
            if (typeof target === 'number') {
                // Width-based uniform scaling
                return { scale: target / originalWidth };
            }
            if ('fit' in target) {
                // Fit within box - use the smaller scale to fit both dimensions
                const scaleW = target.fit / originalWidth;
                const scaleH = target.fit / originalHeight;
                return { scale: Math.min(scaleW, scaleH) };
            }
            if ('width' in target && 'height' in target) {
                // Non-uniform scaling
                return {
                    scaleX: target.width / originalWidth,
                    scaleY: target.height / originalHeight
                };
            }
            if ('width' in target) {
                // Width-based uniform scaling (explicit)
                return { scale: target.width / originalWidth };
            }
            if ('height' in target) {
                // Height-based uniform scaling
                return { scale: target.height / originalHeight };
            }
            return {};
        };

        return {
            to: (targetExpr: ResizeTargetExpr, u: number) => {
                const target = resolveTarget(targetExpr);
                const { scaleX, scaleY, scale } = parseTarget(target);
                const ch = this.channels.get(obj) ?? {};

                if (scale !== undefined) {
                    // Uniform scaling - use current scale for continuity
                    const currentScale = ch.scale ?? this.base.get(obj)!.scale;
                    ch.scale = lerp(currentScale, scale, u);
                } else {
                    // Non-uniform scaling
                    if (scaleX !== undefined) {
                        const current = ch.scaleX ?? 1;
                        ch.scaleX = lerp(current, scaleX, u);
                    }
                    if (scaleY !== undefined) {
                        const current = ch.scaleY ?? 1;
                        ch.scaleY = lerp(current, scaleY, u);
                    }
                }

                this.channels.set(obj, ch);
            },
            by: (deltaExpr: ResizeTargetExpr, u: number) => {
                const delta = resolveTarget(deltaExpr);
                const ch = this.channels.get(obj) ?? {};

                if (typeof delta === 'number') {
                    // Width-based: add delta to current width, scale uniformly
                    const currentScale = ch.scale ?? this.base.get(obj)!.scale;
                    const currentWidth = originalWidth * currentScale;
                    const targetWidth = currentWidth + delta;
                    const targetScale = targetWidth / originalWidth;
                    ch.scale = lerp(currentScale, targetScale, u);
                } else if ('fit' in delta) {
                    // Fit: add delta to current fit box
                    const currentScale = ch.scale ?? this.base.get(obj)!.scale;
                    const currentFit = Math.max(originalWidth, originalHeight) * currentScale;
                    const targetFit = currentFit + delta.fit;
                    const scaleW = targetFit / originalWidth;
                    const scaleH = targetFit / originalHeight;
                    const targetScale = Math.min(scaleW, scaleH);
                    ch.scale = lerp(currentScale, targetScale, u);
                } else if ('width' in delta && 'height' in delta) {
                    // Non-uniform: add deltas to current dimensions
                    const currentScaleX = ch.scaleX ?? 1;
                    const currentScaleY = ch.scaleY ?? 1;
                    const currentWidth = originalWidth * currentScaleX;
                    const currentHeight = originalHeight * currentScaleY;
                    const targetScaleX = (currentWidth + delta.width) / originalWidth;
                    const targetScaleY = (currentHeight + delta.height) / originalHeight;
                    ch.scaleX = lerp(currentScaleX, targetScaleX, u);
                    ch.scaleY = lerp(currentScaleY, targetScaleY, u);
                } else if ('width' in delta) {
                    // Width-based uniform: add delta to current width
                    const currentScale = ch.scale ?? this.base.get(obj)!.scale;
                    const currentWidth = originalWidth * currentScale;
                    const targetScale = (currentWidth + delta.width) / originalWidth;
                    ch.scale = lerp(currentScale, targetScale, u);
                } else if ('height' in delta) {
                    // Height-based uniform: add delta to current height
                    const currentScale = ch.scale ?? this.base.get(obj)!.scale;
                    const currentHeight = originalHeight * currentScale;
                    const targetScale = (currentHeight + delta.height) / originalHeight;
                    ch.scale = lerp(currentScale, targetScale, u);
                }

                this.channels.set(obj, ch);
            }
        };
    }

    fit(obj: SceneObject): FitMethods {
        return {
            to: (bbox: BBoxHandle, u: number) => {
                // Resize to match bbox dimensions
                this.resize(obj).to(bbox.whSize(), u);
                // Pin center to bbox center
                this.pin(obj, 'center').to(bbox.center(), u);
            },
            toBBox: (target: SceneObject | GroupObject, u: number, opts?: FitToBBoxOpts) => {
                // Measure the target
                let bbox = this.bbox(target);
                // Apply padding if specified
                if (opts?.pad !== undefined) {
                    bbox = bbox.pad(opts.pad);
                }
                // Resize and pin
                this.resize(obj).to(bbox.whSize(), u);
                this.pin(obj, 'center').to(bbox.center(), u);
            }
        };
    }

    private computeWorldBounds(obj: SceneObject): Rect {
        if (!this.scene) throw new Error('Scene not set');

        // Materialize any pending deferred pin so bbox/orbit measurements reflect
        // the object's transient position rather than its initial transform.
        this.flushDeferredPin(obj);

        const bounds = this.scene.getBounds(obj);
        const ch = this.channels.get(obj);
        const base = this.base.get(obj)!;

        // Get current position from pin or base
        const pin = this.pins.get(obj);
        let translate: Vec2;
        if (pin) {
            // Pin stores where anchor goes - need to compute actual translate
            const local = this.scene.resolveAnchor(obj, pin.anchor);
            const scale = ch?.scale ?? base.scale;
            const scaleX = (ch?.scaleX ?? 1) * scale;
            const scaleY = (ch?.scaleY ?? 1) * scale;
            const rotate = ch?.rotate ?? base.rotate;

            // Transform local anchor to get offset
            const cos = Math.cos(rotate);
            const sin = Math.sin(rotate);
            const transformedX = local[0] * scaleX * cos - local[1] * scaleY * sin;
            const transformedY = local[0] * scaleX * sin + local[1] * scaleY * cos;

            translate = [pin.world[0] - transformedX, pin.world[1] - transformedY];
        } else {
            translate = base.translate;
        }

        // Get scale factors
        const scale = ch?.scale ?? base.scale;
        const scaleX = (ch?.scaleX ?? 1) * scale;
        const scaleY = (ch?.scaleY ?? 1) * scale;

        // Compute world bounds (axis-aligned, ignoring rotation for simplicity)
        return {
            x: translate[0] + bounds.x * scaleX,
            y: translate[1] + bounds.y * scaleY,
            w: bounds.w * scaleX,
            h: bounds.h * scaleY,
        };
    }

    private computeGroupWorldBounds(group: GroupObject): Rect {
        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        for (const member of group.members) {
            const b = this.computeWorldBounds(member);
            minX = Math.min(minX, b.x);
            minY = Math.min(minY, b.y);
            maxX = Math.max(maxX, b.x + b.w);
            maxY = Math.max(maxY, b.y + b.h);
        }

        if (!isFinite(minX)) {
            return { x: 0, y: 0, w: 0, h: 0 };
        }

        return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }

    bbox(obj: SceneObject | GroupObject): BBoxHandle {
        if (!this.scene) throw new Error('Scene not set');

        // Return a lazy bbox handle that computes rect on demand
        const computeRect = (): Rect => {
            return isGroup(obj)
                ? this.computeGroupWorldBounds(obj)
                : this.computeWorldBounds(obj);
        };

        return new BBoxHandleImpl(computeRect);
    }

    rotate(obj: SceneObject | GroupObject): TransformMethods {
        if (isGroup(obj)) {
            return {
                to: (radExpr: NumExpr, u: number) => {
                    const rad = this.resolveNum(radExpr);
                    // Use stored pivot if available, otherwise calculate from current positions
                    const pivot = this.groupPivots.get(obj.id) ?? this.getGroupCenter(obj);
                    const angle = lerp(0, rad, u);
                    const cos = Math.cos(angle);
                    const sin = Math.sin(angle);

                    for (const member of obj.members) {
                        // Get member's current state
                        const base = this.base.get(member)!;
                        const ch = this.channels.get(member) ?? {};
                        const currentRot = ch.rotate ?? base.rotate;

                        // IMPORTANT: Get member position BEFORE modifying ch.rotate
                        // Otherwise getCurrentAnchorPosition() sees the new rotate value
                        const existingPin = this.pins.get(member);
                        const memberAnchor = existingPin?.anchor ?? 'center';
                        const memberAnchorPos = this.getCurrentAnchorPosition(member, memberAnchor);

                        // 1. Rotate the member itself (like rotating text so letters tilt)
                        ch.rotate = lerp(currentRot, currentRot + rad, u);
                        this.channels.set(member, ch);

                        // 2. Rotate member's position around the pivot (rigid body motion)
                        const offsetFromPivot = vec.sub(memberAnchorPos, pivot);
                        const rotatedOffset: Vec2 = [
                            offsetFromPivot[0] * cos - offsetFromPivot[1] * sin,
                            offsetFromPivot[0] * sin + offsetFromPivot[1] * cos
                        ];
                        const newMemberPos = vec.add(pivot, rotatedOffset);

                        this.pins.set(member, {
                            anchor: memberAnchor,
                            world: newMemberPos,
                        });
                    }
                },
                by: (deltaExpr: NumExpr, u: number) => {
                    const delta = this.resolveNum(deltaExpr);
                    // Use stored pivot if available, otherwise calculate from current positions
                    const pivot = this.groupPivots.get(obj.id) ?? this.getGroupCenter(obj);
                    const angle = delta * u;
                    const cos = Math.cos(angle);
                    const sin = Math.sin(angle);

                    for (const member of obj.members) {
                        // Get member's current state
                        const base = this.base.get(member)!;
                        const ch = this.channels.get(member) ?? {};
                        const currentRot = ch.rotate ?? base.rotate;

                        // IMPORTANT: Get member position BEFORE modifying ch.rotate
                        const existingPin = this.pins.get(member);
                        const memberAnchor = existingPin?.anchor ?? 'center';
                        const memberAnchorPos = this.getCurrentAnchorPosition(member, memberAnchor);

                        // 1. Rotate the member itself
                        ch.rotate = currentRot + delta * u;
                        this.channels.set(member, ch);

                        // 2. Rotate member's position around the pivot
                        const offsetFromPivot = vec.sub(memberAnchorPos, pivot);
                        const rotatedOffset: Vec2 = [
                            offsetFromPivot[0] * cos - offsetFromPivot[1] * sin,
                            offsetFromPivot[0] * sin + offsetFromPivot[1] * cos
                        ];
                        const newMemberPos = vec.add(pivot, rotatedOffset);

                        this.pins.set(member, {
                            anchor: memberAnchor,
                            world: newMemberPos,
                        });
                    }
                }
            };
        }

        return {
            to: (radExpr: NumExpr, u: number) => {
                const rad = this.resolveNum(radExpr);
                const ch = this.channels.get(obj) ?? {};
                // Use current rotation for continuity between tweens
                const current = ch.rotate ?? this.base.get(obj)!.rotate;
                ch.rotate = lerp(current, rad, u);
                this.channels.set(obj, ch);
            },

            by: (deltaExpr: NumExpr, u: number) => {
                const delta = this.resolveNum(deltaExpr);
                const ch = this.channels.get(obj) ?? {};
                const current = ch.rotate ?? this.base.get(obj)!.rotate;
                ch.rotate = current + delta * u;
                this.channels.set(obj, ch);
            }
        };
    }

    pin(obj: SceneObject | GroupObject, anchor: Anchor): PinMethods {
        if (isGroup(obj)) {
            return {
                to: (posExpr: Vec2Expr, u: number) => {
                    const pos = this.resolveVec2(posExpr);
                    // Store the target position as pivot for rotation (especially for 'center')
                    if (anchor === 'center') {
                        this.groupPivots.set(obj.id, pos);
                    }
                    // Get current group anchor position
                    const currentAnchor = this.resolveGroupAnchor(obj, anchor);
                    // Calculate interpolated target
                    const targetAnchor = vec.lerp(currentAnchor, pos, u);
                    // Calculate delta needed to move anchor to target
                    const delta = vec.sub(targetAnchor, currentAnchor);

                    // Apply delta to all members, preserving their individual pin anchors
                    for (const member of obj.members) {
                        const existingPin = this.pins.get(member);
                        const memberAnchor = existingPin?.anchor ?? 'origin';
                        const currentAnchorPos = this.getCurrentAnchorPosition(member, memberAnchor);
                        const newPos = vec.add(currentAnchorPos, delta);
                        this.pins.set(member, {
                            anchor: memberAnchor,
                            world: newPos,
                        });
                    }
                },
                by: (deltaExpr: Vec2Expr, u: number) => {
                    const delta = this.resolveVec2(deltaExpr);
                    const interpolatedDelta = vec.lerp([0, 0], delta, u);

                    // Apply delta to all members, preserving their individual pin anchors
                    for (const member of obj.members) {
                        const existingPin = this.pins.get(member);
                        const memberAnchor = existingPin?.anchor ?? 'origin';
                        const currentAnchorPos = this.getCurrentAnchorPosition(member, memberAnchor);
                        const newPos = vec.add(currentAnchorPos, interpolatedDelta);
                        this.pins.set(member, {
                            anchor: memberAnchor,
                            world: newPos,
                        });
                    }
                }
            };
        }

        return {
            to: (posExpr: Vec2Expr, u: number) => {
                // A motion that has already ended replays its frozen pin instead
                // of re-tracking a target that keeps moving in later motions.
                if (this.applyFrozenPinIfAny(obj, anchor)) {
                    return;
                }

                if (isExpr(posExpr)) {
                    this.deferredPins.set(obj, {
                        anchor,
                        targetExpr: posExpr,
                        u,
                        fromWorld: this.getCurrentAnchorPosition(obj, anchor),
                        segmentId: this.currentSegmentId,
                    });
                    this.notePinnedObject(obj);
                    return;
                }

                const pos = posExpr;
                // Get current world position of this specific anchor for smooth interpolation
                const current = this.getCurrentAnchorPosition(obj, anchor);
                const world = vec.lerp(current, pos, u);

                this.pins.set(obj, {
                    anchor,
                    world,
                });
                this.notePinnedObject(obj);
            },

            by: (deltaExpr: Vec2Expr, u: number) => {
                const delta = this.resolveVec2(deltaExpr);
                // Get current world position of this specific anchor
                const current = this.getCurrentAnchorPosition(obj, anchor);
                const world = vec.add(current, vec.lerp([0, 0], delta, u));

                this.pins.set(obj, {
                    anchor,
                    world,
                });
            }
        };
    }

    opacity(obj: SceneObject | GroupObject): TransformMethods {
        if (isGroup(obj)) {
            return {
                to: (vExpr: NumExpr, u: number) => {
                    const v = this.resolveNum(vExpr);
                    for (const member of obj.members) {
                        const ch = this.channels.get(member) ?? {};
                        const current = ch.opacity ?? this.getInitialOpacity(member);
                        ch.opacity = lerp(current, v, u);
                        this.channels.set(member, ch);
                    }
                },
                by: (deltaExpr: NumExpr, u: number) => {
                    const delta = this.resolveNum(deltaExpr);
                    for (const member of obj.members) {
                        const ch = this.channels.get(member) ?? {};
                        const current = ch.opacity ?? this.getInitialOpacity(member);
                        ch.opacity = current * lerp(1, 1 + delta, u);
                        this.channels.set(member, ch);
                    }
                }
            };
        }

        return {
            to: (vExpr: NumExpr, u: number) => {
                const v = this.resolveNum(vExpr);
                const ch = this.channels.get(obj) ?? {};
                const current = ch.opacity ?? this.getInitialOpacity(obj);
                ch.opacity = lerp(current, v, u);
                this.channels.set(obj, ch);
            },

            by: (deltaExpr: NumExpr, u: number) => {
                const delta = this.resolveNum(deltaExpr);
                const ch = this.channels.get(obj) ?? {};
                const current = ch.opacity ?? this.getInitialOpacity(obj);
                ch.opacity = current * lerp(1, 1 + delta, u);
                this.channels.set(obj, ch);
            }
        };
    }

    group(...objects: SceneObject[]): GroupBuilder {
        // Find existing group with same members (order doesn't matter)
        // This ensures ctx.group(a, b, c) returns the same group across tweens
        // which is critical for pivot tracking in groupPivots
        const membersSet = new Set(objects);
        let groupObj = this.groupCache.find(g =>
            g.members.length === objects.length &&
            g.members.every(m => membersSet.has(m))
        );

        if (!groupObj) {
            groupObj = {
                id: Symbol('group'),
                members: objects,
                isGroup: true as const,
            };
            this.groupCache.push(groupObj);
        }

        // Create builder with fluent methods that call back to this context
        // Debug tracking for groups is deferred - we track when animated via trackGroupDebug
        const self = this;
        const builder: GroupBuilder = {
            ...groupObj,
            pin: (anchor: Anchor) => {
                const methods = self.pin(groupObj, anchor);
                return {
                    to: (pos: Vec2, u: number) => {
                        methods.to(pos, u);
                        self.trackGroupDebug(groupObj, u, { anchor, target: pos });
                    },
                    by: (delta: Vec2, u: number) => {
                        methods.by(delta, u);
                        self.trackGroupDebug(groupObj, u);
                    },
                };
            },
            scale: () => {
                const methods = self.scale(groupObj);
                return {
                    to: (v: number, u: number) => {
                        methods.to(v, u);
                        self.trackGroupDebug(groupObj, u);
                    },
                    by: (delta: number, u: number) => {
                        methods.by(delta, u);
                        self.trackGroupDebug(groupObj, u);
                    },
                };
            },
            rotate: () => {
                const methods = self.rotate(groupObj);
                return {
                    to: (v: number, u: number) => {
                        methods.to(v, u);
                        self.trackGroupDebug(groupObj, u);
                    },
                    by: (delta: number, u: number) => {
                        methods.by(delta, u);
                        self.trackGroupDebug(groupObj, u);
                    },
                };
            },
            opacity: () => {
                const methods = self.opacity(groupObj);
                return {
                    to: (v: number, u: number) => {
                        methods.to(v, u);
                        self.trackGroupDebug(groupObj, u);
                    },
                    by: (delta: number, u: number) => {
                        methods.by(delta, u);
                        self.trackGroupDebug(groupObj, u);
                    },
                };
            },
        };

        return builder;
    }

    private trackGroupDebug(groupObj: GroupObject, u: number, pin?: { anchor: Anchor; target: Vec2 }): DebugGroupInfo | null {
        // Only track during active animation, not at completion
        if (u < 1) {
            // Find existing or create new
            let entry = this.debugGroups.find(
                g => g.kind === 'group' && g.members === groupObj.members
            );
            if (!entry) {
                entry = {
                    members: groupObj.members,
                    kind: 'group',
                };
                this.debugGroups.push(entry);
            }
            if (pin) {
                entry.pin = pin;
            }
            return entry;
        }
        return null;
    }

    /** Get bounds for an ObjRef (single object or group) */
    private getObjRefBounds(obj: ObjRef): Rect {
        if (isGroup(obj)) {
            return this.computeGroupWorldBounds(obj);
        }
        return this.computeWorldBounds(obj);
    }

    /** Get the top-left position of an ObjRef (bounds position, not pin position) */
    private getObjRefPosition(obj: ObjRef): Vec2 {
        const bounds = this.getObjRefBounds(obj);
        return [bounds.x, bounds.y];
    }

    /** Move an ObjRef by a delta (moves all members of a group together, preserving pin anchors) */
    private moveObjRef(obj: ObjRef, fromPos: Vec2, toPos: Vec2, u: number): void {
        const delta = vec.sub(toPos, fromPos);
        const interpolatedDelta = vec.lerp([0, 0], delta, u);

        if (isGroup(obj)) {
            for (const member of obj.members) {
                // Preserve existing pin anchor, or default to 'origin'
                const existingPin = this.pins.get(member);
                const anchor = existingPin?.anchor ?? 'origin';
                const currentAnchorPos = this.getCurrentAnchorPosition(member, anchor);
                const newPos = vec.add(currentAnchorPos, interpolatedDelta);
                this.pins.set(member, {
                    anchor,
                    world: newPos,
                });
            }
        } else {
            // Preserve existing pin anchor, or default to 'origin'
            const existingPin = this.pins.get(obj);
            const anchor = existingPin?.anchor ?? 'origin';
            const currentAnchorPos = this.getCurrentAnchorPosition(obj, anchor);
            const newPos = vec.add(currentAnchorPos, interpolatedDelta);
            this.pins.set(obj, {
                anchor,
                world: newPos,
            });
        }
    }

    private alignOnCrossAxis(
        start: number,
        size: number,
        maxSize: number,
        align: Anchor
    ): number {
        if (align === "center") {
            return start + (maxSize - size) / 2;
        }
        if (align === "topLeft" || align === "left" || align === "top") {
            return start;
        }
        if (align === "bottomRight" || align === "right" || align === "bottom") {
            return start + maxSize - size;
        }
        // Baseline alignment (for text) - treat as bottom for now
        if (align === "baselineLeft" || align === "baseline") {
            return start + maxSize - size;
        }
        return start;
    }

    private calculateLayoutPositions(
        objects: ObjRef[],
        axis: "x" | "y",
        align: Anchor,
        gap: number
    ): Vec2[] {
        if (!this.scene) throw new Error('Scene not set');

        // Get current bounds for each object (groups use their combined bounds)
        const boundsList = objects.map(obj => this.getObjRefBounds(obj));

        // Find the starting position (leftmost/topmost of current positions)
        const startX = Math.min(...boundsList.map(b => b.x));
        const startY = Math.min(...boundsList.map(b => b.y));

        // Calculate cross-axis alignment reference
        const maxH = Math.max(...boundsList.map(b => b.h));
        const maxW = Math.max(...boundsList.map(b => b.w));

        const targets: Vec2[] = [];
        let cursor = axis === "x" ? startX : startY;

        for (let i = 0; i < objects.length; i++) {
            const b = boundsList[i]!;
            let x: number, y: number;

            if (axis === "x") {
                x = cursor;
                y = this.alignOnCrossAxis(startY, b.h, maxH, align);
                cursor += b.w + gap;
            } else {
                x = this.alignOnCrossAxis(startX, b.w, maxW, align);
                y = cursor;
                cursor += b.h + gap;
            }

            targets.push([x, y]);
        }

        return targets;
    }

    layout(...args: (ObjRef | LayoutOpts)[]): LayoutBuilder {
        // Parse args - last arg may be LayoutOpts
        let opts: LayoutOpts = {};
        let objects: ObjRef[];

        const last = args[args.length - 1];
        if (last && typeof last === 'object' && !('id' in last)) {
            opts = last as LayoutOpts;
            objects = args.slice(0, -1) as ObjRef[];
        } else {
            objects = args as ObjRef[];
        }

        const axis = opts.axis ?? "x";
        const align = opts.align ?? "center";
        const gap = opts.gap ?? 0;

        return {
            to: (u: number): GroupBuilder => {
                // Calculate target positions for each ObjRef (groups stay as units)
                const targets = this.calculateLayoutPositions(objects, axis, align, gap);

                // Move each object/group to its target position
                for (let i = 0; i < objects.length; i++) {
                    const obj = objects[i]!;
                    const currentPos = this.getObjRefPosition(obj);
                    const targetPos = targets[i]!;
                    this.moveObjRef(obj, currentPos, targetPos, u);
                }

                // Flatten to get all scene objects for the result group
                const allMembers = flattenObjRefs(objects);

                // Create group for chaining
                const groupObj: GroupObject = {
                    id: Symbol('layout-group'),
                    members: allMembers,
                    isGroup: true as const,
                };

                // Track for debug overlay as layout (only during active animation, not at completion)
                let debugEntry: DebugGroupInfo | null = null;
                if (u < 1) {
                    debugEntry = {
                        members: allMembers,
                        kind: 'layout',
                    };
                    this.debugGroups.push(debugEntry);
                }

                // Return GroupBuilder for chaining with debug tracking
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const self = this;
                const builder: GroupBuilder = {
                    ...groupObj,
                    pin: (anchor: Anchor) => {
                        const methods = self.pin(groupObj, anchor);
                        return {
                            to: (pos: Vec2, pinU: number) => {
                                methods.to(pos, pinU);
                                if (debugEntry) {
                                    debugEntry.pin = { anchor, target: pos };
                                }
                            },
                            by: (delta: Vec2, pinU: number) => {
                                methods.by(delta, pinU);
                            },
                        };
                    },
                    scale: () => self.scale(groupObj),
                    rotate: () => self.rotate(groupObj),
                    opacity: () => self.opacity(groupObj),
                };

                return builder;
            }
        };
    }

    inline(...args: (ObjRef | number)[]): LayoutBuilder {
        const { objects, gap } = this.parseLayoutArgs(args);
        return this.layout(...objects, { axis: "x", align: "baselineLeft", gap });
    }

    stack(...args: (ObjRef | number)[]): LayoutBuilder {
        const { objects, gap } = this.parseLayoutArgs(args);
        return this.layout(...objects, { axis: "y", align: "center", gap });
    }

    paragraph(...args: (ObjRef | number)[]): LayoutBuilder {
        const { objects, gap } = this.parseLayoutArgs(args);
        return this.layout(...objects, { axis: "y", align: "left", gap });
    }

    /** Parse layout args - last arg may be a gap number */
    private parseLayoutArgs(args: (ObjRef | number)[]): { objects: ObjRef[], gap: number } {
        const last = args[args.length - 1];
        if (typeof last === 'number') {
            return { objects: args.slice(0, -1) as ObjRef[], gap: last };
        }
        return { objects: args as ObjRef[], gap: 0 };
    }

    /** Get anchor offset within bounds */
    private getAnchorOffsetFromRect(bounds: Rect, anchor: Anchor): Vec2 {
        let ox = 0, oy = 0;

        if (anchor === 'top' || anchor === 'center' || anchor === 'bottom' || anchor === 'baseline') {
            ox = bounds.w / 2;
        } else if (anchor === 'topRight' || anchor === 'right' || anchor === 'bottomRight') {
            ox = bounds.w;
        }

        if (anchor === 'left' || anchor === 'center' || anchor === 'right') {
            oy = bounds.h / 2;
        } else if (anchor === 'bottomLeft' || anchor === 'bottom' || anchor === 'bottomRight' || anchor === 'baselineLeft' || anchor === 'baseline') {
            oy = bounds.h;
        }

        return [ox, oy];
    }

    /** Get anchor position in world space for an ObjRef */
    private getObjRefAnchorPosition(obj: ObjRef, anchor: Anchor): Vec2 {
        const bounds = this.getObjRefBounds(obj);
        const offset = this.getAnchorOffsetFromRect(bounds, anchor);
        return [bounds.x + offset[0], bounds.y + offset[1]];
    }

    align(...args: (ObjRef | AlignOpts)[]): LayoutBuilder {
        // Parse args - last arg may be AlignOpts
        let opts: AlignOpts = {};
        let objects: ObjRef[];

        const last = args[args.length - 1];
        if (last && typeof last === 'object' && !('id' in last)) {
            opts = last as AlignOpts;
            objects = args.slice(0, -1) as ObjRef[];
        } else {
            objects = args as ObjRef[];
        }

        const anchor = opts.anchor ?? "center";

        return {
            to: (u: number): GroupBuilder => {
                if (!this.scene || objects.length === 0) {
                    const allMembers = flattenObjRefs(objects);
                    return this.group(...allMembers);
                }

                // Get the first object's anchor position as the reference
                const targetAnchorPos = this.getObjRefAnchorPosition(objects[0]!, anchor);

                // Move all other objects so their anchor aligns with the first object's anchor
                for (let i = 1; i < objects.length; i++) {
                    const obj = objects[i]!;
                    const bounds = this.getObjRefBounds(obj);
                    const anchorOffset = this.getAnchorOffsetFromRect(bounds, anchor);

                    // Target top-left position = target anchor position - anchor offset within bounds
                    const targetPos: Vec2 = [
                        targetAnchorPos[0] - anchorOffset[0],
                        targetAnchorPos[1] - anchorOffset[1]
                    ];

                    const currentPos = this.getObjRefPosition(obj);
                    this.moveObjRef(obj, currentPos, targetPos, u);
                }

                // Flatten to get all scene objects for the result group
                const allMembers = flattenObjRefs(objects);

                // Create group for chaining
                const groupObj: GroupObject = {
                    id: Symbol('align-group'),
                    members: allMembers,
                    isGroup: true as const,
                };

                // Track for debug overlay
                let debugEntry: DebugGroupInfo | null = null;
                if (u < 1) {
                    debugEntry = {
                        members: allMembers,
                        kind: 'layout',
                    };
                    this.debugGroups.push(debugEntry);
                }

                // Return GroupBuilder for chaining
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const self = this;
                return {
                    ...groupObj,
                    pin: (pinAnchor: Anchor) => {
                        const methods = self.pin(groupObj, pinAnchor);
                        return {
                            to: (pos: Vec2, pinU: number) => {
                                methods.to(pos, pinU);
                                if (debugEntry) {
                                    debugEntry.pin = { anchor: pinAnchor, target: pos };
                                }
                            },
                            by: (delta: Vec2, pinU: number) => {
                                methods.by(delta, pinU);
                            },
                        };
                    },
                    scale: () => self.scale(groupObj),
                    rotate: () => self.rotate(groupObj),
                    opacity: () => self.opacity(groupObj),
                };
            }
        };
    }

    center(...objects: ObjRef[]): LayoutBuilder {
        return this.align(...objects, { anchor: "center" });
    }

    solve(scene: SceneGeometry): ResolvedCtx {
        const resolved = new Map<SceneObject, Transform>();
        const opacity = new Map<SceneObject, number>();
        const props = new Map<SceneObject, Record<string, number>>();

        for (const obj of scene.objects()) {
            const base = scene.getInitialTransform(obj);

            const ch = this.channels.get(obj);
            const scale = ch?.scale ?? base.scale;
            const scaleX = ch?.scaleX;
            const scaleY = ch?.scaleY;
            const rotate = ch?.rotate ?? base.rotate;

            let translate = base.translate ?? [0, 0] as const;
            const pin = this.pins.get(obj);
            if (pin) {
                const local = scene.resolveAnchor(obj, pin.anchor);
                const transformed = applySR(local, scale, rotate, scaleX ?? 1, scaleY ?? 1);
                translate = vec.sub(pin.world, transformed);
            }

            resolved.set(obj, {
                translate,
                rotate: rotate ?? 0,
                scale: scale ?? 1,
                scaleX,
                scaleY,
            });
            opacity.set(obj, ch?.opacity ?? scene.getInitialOpacity?.(obj) ?? 1);
            if (ch?.props) props.set(obj, ch.props);
        }

        return {
            getTransform: (o) => resolved.get(o)!,
            getOpacity: (o) => opacity.get(o) ?? 1,
            getProp: (o, name) => props.get(o)?.[name],
        };
    }

    prop(obj: SceneObject, name: string): TransformMethods {
        return {
            to: (vExpr: NumExpr, u: number) => {
                const v = this.resolveNum(vExpr);
                const ch = this.channels.get(obj) ?? {};
                const props = ch.props ?? {};
                const current = props[name] ?? this.getInitialProp(obj, name) ?? v;
                props[name] = lerp(current, v, u);
                ch.props = props;
                this.channels.set(obj, ch);
            },
            by: (deltaExpr: NumExpr, u: number) => {
                const delta = this.resolveNum(deltaExpr);
                const ch = this.channels.get(obj) ?? {};
                const props = ch.props ?? {};
                const current = props[name] ?? 0;
                props[name] = current + delta * u;
                ch.props = props;
                this.channels.set(obj, ch);
            },
        };
    }

    select(...objects: ObjRef[]): Selection {
        return new SelectionImpl(this, objects);
    }
}

// ─── SelectionImpl ────────────────────────────────────────────────────────────
// Implements the selector → animation chaining API.
// Single SceneObject selections use the direct (non-group) ctx methods so that
// rotate/scale semantics match ("rotate TO angle" vs "rotate BY angle").
// Multi-object selections delegate to group methods.

class SelectionImpl implements Selection {
    constructor(private ctx: AnimCtxImpl, private _objects: ObjRef[]) {}

    get objects(): ObjRef[] { return this._objects; }

    /** True when selection contains exactly one plain SceneObject */
    private get isSingle(): boolean {
        return this._objects.length === 1 && !isGroup(this._objects[0]!);
    }

    private get singleObj(): SceneObject {
        return this._objects[0] as SceneObject;
    }

    private asGroupObject(): GroupObject {
        // ctx.group() caches by member set — stable within a frame
        return this.ctx.group(...flattenObjRefs(this._objects));
    }

    private forEachSceneObj(fn: (obj: SceneObject) => void): void {
        for (const obj of flattenObjRefs(this._objects)) fn(obj);
    }

    // ── Layout ────────────────────────────────────────────────────────────────

    layout(opts: LayoutOpts, u: number): Selection {
        const args: (ObjRef | LayoutOpts)[] = [...this._objects, opts];
        this.ctx.layout(...args).to(u);
        return this;
    }

    inline(u: number): Selection {
        const args: (ObjRef | number)[] = [...this._objects];
        this.ctx.inline(...args).to(u);
        return this;
    }

    stack(u: number): Selection {
        const args: (ObjRef | number)[] = [...this._objects];
        this.ctx.stack(...args).to(u);
        return this;
    }

    paragraph(u: number): Selection {
        const args: (ObjRef | number)[] = [...this._objects];
        this.ctx.paragraph(...args).to(u);
        return this;
    }

    align(anchor: Anchor, u: number): Selection {
        const args: (ObjRef | AlignOpts)[] = [...this._objects, { anchor }];
        this.ctx.align(...args).to(u);
        return this;
    }

    // ── Positioning ───────────────────────────────────────────────────────────

    pin(anchor: Anchor, pos: Vec2Expr, u: number): Selection {
        if (this.isSingle) {
            this.ctx.pin(this.singleObj, anchor).to(pos, u);
        } else {
            this.ctx.pin(this.asGroupObject(), anchor).to(pos, u);
        }
        return this;
    }

    pinBy(anchor: Anchor, delta: Vec2Expr, u: number): Selection {
        if (this.isSingle) {
            this.ctx.pin(this.singleObj, anchor).by(delta, u);
        } else {
            this.ctx.pin(this.asGroupObject(), anchor).by(delta, u);
        }
        return this;
    }

    // ── Group-aware transforms ────────────────────────────────────────────────

    rotate(rad: NumExpr, u: number): Selection {
        if (this.isSingle) {
            this.ctx.rotate(this.singleObj).to(rad, u);
        } else {
            this.ctx.rotate(this.asGroupObject()).to(rad, u);
        }
        return this;
    }

    rotateBy(deltaRad: NumExpr, u: number): Selection {
        if (this.isSingle) {
            this.ctx.rotate(this.singleObj).by(deltaRad, u);
        } else {
            this.ctx.rotate(this.asGroupObject()).by(deltaRad, u);
        }
        return this;
    }

    scale(v: NumExpr, u: number): Selection {
        if (this.isSingle) {
            this.ctx.scale(this.singleObj).to(v, u);
        } else {
            this.ctx.scale(this.asGroupObject()).to(v, u);
        }
        return this;
    }

    opacity(v: NumExpr, u: number): Selection {
        if (this.isSingle) {
            this.ctx.opacity(this.singleObj).to(v, u);
        } else {
            this.ctx.opacity(this.asGroupObject()).to(v, u);
        }
        return this;
    }

    // ── Per-object transforms ─────────────────────────────────────────────────

    scaleX(v: NumExpr, u: number): Selection {
        this.forEachSceneObj(obj => this.ctx.scaleX(obj).to(v, u));
        return this;
    }

    scaleY(v: NumExpr, u: number): Selection {
        this.forEachSceneObj(obj => this.ctx.scaleY(obj).to(v, u));
        return this;
    }

    resize(target: ResizeTargetExpr, u: number): Selection {
        this.forEachSceneObj(obj => this.ctx.resize(obj).to(target, u));
        return this;
    }

    width(v: NumExpr, u: number): Selection {
        this.forEachSceneObj(obj => this.ctx.width(obj).to(v, u));
        return this;
    }

    height(v: NumExpr, u: number): Selection {
        this.forEachSceneObj(obj => this.ctx.height(obj).to(v, u));
        return this;
    }

    // ── Fitting ───────────────────────────────────────────────────────────────

    fit(bbox: BBoxHandle, u: number): Selection {
        this.forEachSceneObj(obj => this.ctx.fit(obj).to(bbox, u));
        return this;
    }

    fitToBBox(target: Selection | ObjRef, u: number, opts?: FitToBBoxOpts): Selection {
        const targetRef: ObjRef = target instanceof SelectionImpl
            ? target.asGroupObject()
            : target as ObjRef;
        this.forEachSceneObj(obj => this.ctx.fit(obj).toBBox(targetRef, u, opts));
        return this;
    }

    // ── Measurement ───────────────────────────────────────────────────────────

    bbox(pad?: Padding): BBoxHandle {
        const handle = this.ctx.bbox(this.asGroupObject());
        return pad !== undefined ? handle.pad(pad) : handle;
    }

    // ── Generic property channels ─────────────────────────────────────────────

    prop(name: string, value: NumExpr, u: number): Selection {
        this.forEachSceneObj(obj => this.ctx.prop(obj, name).to(value, u));
        return this;
    }
}
